import CircleIcon from '@mui/icons-material/Circle';
import { Box, Button, Grid, Stack, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { CarouselProductDetailImage } from '@app/components/Company/CarouselProductDetailImage';
import { EventInfo } from '@app/components/Product/EventInfo';
import { ExhibitionFloorPlan } from '@app/components/Product/ExhibitionFloorPlan';
import { ExhibitionInfo } from '@app/components/Product/ExhibitionInfo';
import { ExhibitionInfoSp } from '@app/components/Product/ExhibitionInfoSp';
import { LegalLimitationInfo } from '@app/components/Product/LegalLimitationInfo';
import { OrganizationInfo } from '@app/components/Product/OrganizationInfo';
import { PropertyFloorPlan } from '@app/components/Product/PropertyFloorPlan';
import { PropertyInfo } from '@app/components/Product/PropertyInfo';
import { PropertyPlotMap } from '@app/components/Product/PropertyPlotMap';
import { PropertySalesInfo } from '@app/components/Product/PropertySalesInfo';
import { PropertyVariantSkuDetails } from '@app/components/Product/PropertyVariantSkuDetails';
import { PublishInfo } from '@app/components/Product/PublishInfo';
import { SalesScheduleInfo } from '@app/components/Product/SalesScheduleInfo';
import { Map } from '@app/components/Shared/Map';
import { Tag } from '@app/components/Shared/Tag';
import { attributesBySmaverseSelector } from '@app/domain/catalog';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { GA_CUSTOM_EVENT, GA_CUSTOM_EVENT_SUFFIX } from '@app/static/constants';
import { Product, ProductVariantSku } from '@app/types/catalog';
import { Organization } from '@app/types/organization';
import {
  checkSmaverseSupport,
  getBuildingStatus,
  getPropertyCategory,
} from '@app/utils/catalog';
import { generateEventName, generateEventNameSuffix } from '@app/utils/event';

interface ProductDetailsProps {
  isExhibition: boolean;
  product: Product;
}

function handleButtonClick(
  navigate: ReturnType<typeof useNavigate>,
  productId: string | undefined,
  isExhibition: boolean
) {
  const path = isExhibition
    ? '/exhibitions/:productId/inquiry/create'
    : '/properties/:productId/inquiry/create';
  if (productId) {
    navigate(path.replace(':productId', productId));
  }
}

function sendGAEvent(eventName: string, isMobile: boolean, suffix?: string) {
  const eventNameWithSuffix = suffix
    ? generateEventNameSuffix(generateEventName(eventName, isMobile), suffix)
    : generateEventName(eventName, isMobile);
  TagManager.dataLayer({
    dataLayer: {
      event: eventNameWithSuffix,
    },
  });
}

export function ProductDetails({ product, isExhibition }: ProductDetailsProps) {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const smaverseAttributes = useRecoilValue(attributesBySmaverseSelector);
  const isSmaverse = useMemo(() => {
    return checkSmaverseSupport(product.attributes || [], smaverseAttributes);
  }, [product.attributes, smaverseAttributes]);
  const theme = useTheme();
  const plotMapVariants = product.variants.filter(
    (variant) => variant.sku === ProductVariantSku.PLOT_MAP
  );

  const floorPlanVariants = product.variants.filter(
    (variant) => variant.sku === ProductVariantSku.FLOOR_PLAN
  );

  const relevantVariants = product.variants.filter(
    (variant) =>
      variant.sku === ProductVariantSku.EXTERIOR ||
      variant.sku === ProductVariantSku.AROUND ||
      variant.sku === ProductVariantSku.EQUIPMENT ||
      variant.sku === ProductVariantSku.INTERIOR
  );

  const productAddress = `${product.addressLine1}${product.addressLine2}${
    !!product.customFields.addressLine3HiddenFlag && product.addressLine3
      ? ` ${product.addressLine3}`
      : ''
  }`;

  return (
    <Stack sx={{ width: '100%' }} alignItems="center">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        bgcolor={theme.palette.blueAccent.main}
        px={2}
        py={1}
        sx={{
          display: {
            sm: 'none',
            xs: 'flex',
          },
          position: 'sticky',
          top: 0,
          width: '100%',
          zIndex: 10,
        }}
      >
        <Typography
          variant="body2"
          color="white"
          fontSize="1rem"
          width="100%"
          pr={4}
        >
          専門スタッフがご対応。
          最新の空間を体験しながら住まいづくりのプロに出会えます。
        </Typography>

        <Button
          sx={{
            bgcolor: 'white',
            fontSize: 'clamp(1.125rem, 1.0568rem + 0.28vw, 1.25rem)',
            padding: 1,
            width: 130,
          }}
          color="black"
          fullWidth
          onClick={() => {
            handleButtonClick(navigate, product.id, isExhibition);
            //Send GA Event
            let eventName = GA_CUSTOM_EVENT.PROPERTIES_TOUR_RESERVATION;
            if (isExhibition) {
              eventName = GA_CUSTOM_EVENT.EXHIBITION_TOUR_RESERVATION;
            }
            sendGAEvent(eventName, isMobile, GA_CUSTOM_EVENT_SUFFIX.RIGHT);
          }}
        >
          見学を
          <br />
          予約する
        </Button>
      </Stack>
      <Box borderBottom={1} borderColor="#E0E0E0" py={4} width="100%" px={2}>
        <Stack
          direction={{ sm: 'row', xs: 'column-reverse' }}
          justifyContent="space-between"
          maxWidth={1040}
          mx="auto"
          spacing={{ sm: 0, xs: 2 }}
        >
          <Typography
            variant="h2"
            fontWeight={600}
            px={2}
            sx={{
              flex: { sm: 2 },
              maxWidth: { sm: 580, xs: '100%' },
            }}
          >
            {product.name}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            bgcolor={theme.palette.blueAccent.main}
            borderRadius="15px"
            px={2.5}
            py={1.5}
            sx={{
              display: {
                sm: 'flex',
                xs: 'none',
              },
              width: 390,
            }}
          >
            <Typography
              variant="body2"
              color="white"
              fontSize="1rem"
              maxWidth={230}
            >
              専門スタッフがご対応。
              最新の空間を体験しながら住まいづくりのプロに出会えます。
            </Typography>

            <Button
              sx={{
                bgcolor: 'white',
                fontSize: 'clamp(1.125rem, 1.0568rem + 0.28vw, 1.25rem)',
                padding: 1,
                width: 100,
              }}
              color="black"
              fullWidth
              onClick={() => {
                handleButtonClick(navigate, product.id, isExhibition);
                //Send GA Event
                let eventName = GA_CUSTOM_EVENT.PROPERTIES_TOUR_RESERVATION;
                if (isExhibition) {
                  eventName = GA_CUSTOM_EVENT.EXHIBITION_TOUR_RESERVATION;
                }
                sendGAEvent(eventName, isMobile, GA_CUSTOM_EVENT_SUFFIX.CENTER);
              }}
            >
              見学を
              <br />
              予約する
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Stack
        width="100%"
        maxWidth={1040}
        mx="auto"
        mt={{ sm: 4, xs: 2 }}
        gap={2}
        px={2}
      >
        <Stack direction="row" gap={1}>
          {!isExhibition && (
            <>
              <Tag
                text={getPropertyCategory(
                  product.customFields.propertyCategory
                )}
              />
              <Tag
                text={getBuildingStatus(product.customFields.buildingStatus)}
              />
            </>
          )}
          {typeof product.organization === 'object' &&
            product.organization !== null && (
              <Typography
                variant="body1"
                color={theme.palette.grey[500]}
                ml={2}
              >
                {productAddress}
              </Typography>
            )}
        </Stack>
        <Stack
          width="100%"
          sx={{
            backgroundColor: '#F5F8FC',
            border: 'solid 1px #e0e0e0',
            borderRadius: '16px',
            padding: 4,
          }}
        >
          <CarouselProductDetailImage
            images={product.images.map((item) => item.url)}
            isSmaverse={isSmaverse}
          />
        </Stack>
      </Stack>
      <Box
        bgcolor="#f5f8fc"
        width="100%"
        minHeight={500}
        mt={{ md: 9, sm: 7, xs: 4 }}
        py={{ md: 9, sm: 7, xs: 4 }}
        px={2}
      >
        <Grid
          container
          spacing={4}
          maxWidth={1040}
          mx="auto"
          width="100%"
          sx={{ mt: 0 }}
        >
          <Grid
            item
            mb={{ sm: 0, xs: 4 }}
            xs={12}
            sm={isExhibition ? 12 : 6}
            borderRight={{
              sm: isExhibition
                ? 0
                : `1px solid ${theme.palette.blueAccent.main}`,
              xs: 0,
            }}
            sx={{
              '&.MuiGrid-item': {
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: { sm: 4, xs: 0 },
                paddingTop: 0,
              },
              paddingTop: 0,
            }}
          >
            <Stack width="100%">
              <Typography
                bgcolor={theme.palette.blueAccent.main}
                color="white"
                px={2}
                height={36}
                fontWeight={600}
                borderRadius={15}
                display="flex"
                alignItems="center"
                variant="h6"
              >
                物件の特徴
              </Typography>

              <Stack mt={2} gap={1}>
                {product.features
                  .filter((feature) => feature.value)
                  .map((text, idx) => (
                    <Stack
                      key={idx}
                      direction="row"
                      alignItems="flex-start"
                      gap={1}
                    >
                      <CircleIcon
                        sx={{ color: '#00a0ff', fontSize: 16, mt: 0.5 }}
                      />
                      <Typography>{text.value}</Typography>
                    </Stack>
                  ))}
              </Stack>

              <Typography
                color={theme.palette.blueAccent.main}
                fontWeight={500}
                py={3}
                variant="title1"
              >
                {product.additionalInformation}
              </Typography>

              <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                {product.description}
              </Typography>
            </Stack>
          </Grid>
          {!isExhibition && (
            <Grid
              item
              xs={12}
              sm={6}
              sx={{
                '&.MuiGrid-item': {
                  paddingBottom: 0,
                  paddingLeft: { sm: 4, xs: 0 },
                  paddingRight: 0,
                  paddingTop: 0,
                },
                paddingTop: 0,
              }}
            >
              <Stack width="100%">
                <PropertySalesInfo product={product} />
                <PropertyPlotMap variants={plotMapVariants} />
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
      {isExhibition && floorPlanVariants.length > 0 && (
        <Box px={2} width="100%">
          <ExhibitionFloorPlan variants={floorPlanVariants} />
        </Box>
      )}

      {!isExhibition && (
        <Box px={2} width="100%">
          <PropertyFloorPlan
            productId={product.id}
            organizationId={product.organizationId}
            variants={floorPlanVariants}
            parentOrganizationId={
              (product.organization as Organization)?.parentId || ''
            }
          />
        </Box>
      )}
      {!isExhibition && relevantVariants.length > 0 && (
        <PropertyVariantSkuDetails variants={relevantVariants} />
      )}
      {!!product.geoCoordinatesField?.coordinates?.[0] && (
        <Box width="100%" bgcolor="#f5f8fc" py={{ sm: 9, xs: 4 }} px={2}>
          <Stack width="100%" maxWidth={1040} mx="auto" gap={4}>
            <Typography
              width="100%"
              bgcolor={theme.palette.blueAccent.main}
              color="white"
              px={2}
              height={36}
              fontWeight={600}
              borderRadius={15}
              display="flex"
              alignItems="center"
              variant="h6"
            >
              住所
            </Typography>
            <Box px={1}>
              <Map
                coordinates={product.geoCoordinatesField.coordinates}
                address={productAddress}
              />
            </Box>
          </Stack>
        </Box>
      )}

      {isExhibition && isMobile && <ExhibitionInfoSp product={product} />}
      {isExhibition && !isMobile && <ExhibitionInfo product={product} />}
      {!isExhibition && (
        <Box width="100%" bgcolor="white" py={{ sm: 9, xs: 4 }} px={2}>
          <Stack width="100%" maxWidth={1040} mx="auto" mt={0}>
            <SalesScheduleInfo product={product} />
            <PropertyInfo product={product} />
            <LegalLimitationInfo product={product} />
            <EventInfo product={product} />
            <OrganizationInfo product={product} />
            <PublishInfo product={product} />
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
